import React, { useState, useEffect } from "react";
import {
  Heart,
  Calendar,
  Menu,
  X,
  MapPin,
  ExternalLink,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import cjs from "./assets/cjs.jpg";
import franklin from "./assets/franklin.jpg";
import img1 from "./assets/img1.jpg";
import img2 from "./assets/img2.jpg";
import img3 from "./assets/img3.jpg";
import img4 from "./assets/img4.jpg";
import img5 from "./assets/img5.jpg";

// Load custom fonts
const fontLink = document.createElement("link");
fontLink.href =
  "https://fonts.googleapis.com/css2?family=Alex+Brush&family=Lora:wght@400;500;600&family=Montserrat:wght@300;400;500&display=swap";
fontLink.rel = "stylesheet";
document.head.appendChild(fontLink);

// Supabase Configuration - REPLACE WITH YOUR ACTUAL ANON KEY
const SUPABASE_URL = "https://gfrpqhkwpqaispkgorms.supabase.co";
const SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdmcnBxaGt3cHFhaXNwa2dvcm1zIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjkyMTE2MDQsImV4cCI6MjA4NDc4NzYwNH0.B4Y8XWCwWCsw8iEbPYSUhnICrXMIfCb9ypHW9TZM_8I";

// Simple Supabase client implementation
const supabase = {
  async rpc(functionName, params) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${functionName}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(params),
    });

    const data = await res.json().catch(() => null);
    return { data, error: !res.ok ? data : null };
  },

  from(table) {
    return {
      // SELECT queries
      select(columns = "*") {
        return {
          eq: async (column, value) => {
            const res = await fetch(
              `${SUPABASE_URL}/rest/v1/${table}?${column}=eq.${value}&select=${columns}`,
              {
                headers: {
                  apikey: SUPABASE_ANON_KEY,
                  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
                },
              },
            );
            let data = [];
            try {
              data = await res.json();
            } catch (err) {
              data = [];
            }
            return {
              data: Array.isArray(data) ? data : [data],
              error: !res.ok ? data : null,
            };
          },
        };
      },

      // INSERT
      async insert(values) {
        const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            Prefer: "return=representation",
          },
          body: JSON.stringify(values),
        });

        let data = [];
        try {
          data = await res.json();
        } catch (err) {
          data = [];
        }
        return {
          data: Array.isArray(data) ? data : [data],
          error: !res.ok ? data : null,
        };
      },

      // UPDATE
      update(values) {
        return {
          eq: async (column, value) => {
            const res = await fetch(
              `${SUPABASE_URL}/rest/v1/${table}?${column}=eq.${value}`,
              {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json",
                  apikey: SUPABASE_ANON_KEY,
                  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
                  Prefer: "return=representation",
                },
                body: JSON.stringify(values),
              },
            );

            let data = [];
            try {
              data = await res.json();
            } catch (err) {
              data = [];
            }
            return {
              data: Array.isArray(data) ? data : [data],
              error: !res.ok ? data : null,
            };
          },
        };
      },
    };
  },
};

const FAQSection = ({ backgroundImage }) => {
  const faqs = [
    {
      question: "What should I wear?",
      answer:
        "We're planning a semi-formal celebration. For men, suits or dress pants with a button-down shirt and tie are perfect. For women, cocktail dresses, dressy jump-suits, or dressy two piece-outifts work beautifully.",
    },
    {
      question: "Will the ceremony and reception be at the same location?",
      answer:
        "Yes! Both the ceremony and reception will take place at CJ's Off the Square in Franklin, Tennessee. You won't need to travel between venues.",
    },
    {
      question: "Can I bring a plus-one?",
      answer:
        "We've carefully crafted our guest list based on our venue capacity. If your invitation includes a plus-one, their name will be listed on your invitation. Please check your RSVP section to see who is invited from your party.",
    },
    {
      question: "Are children welcome?",
      answer:
        "We love your little ones, but we've planned an adults-only celebration to allow all our guests to relax and enjoy the evening. We hope this gives parents a wonderful opportunity for a night out!",
    },
    {
      question: "What time should I arrive?",
      answer:
        "Please plan to arrive 15-20 minutes before the ceremony start time (which will be shared closer to the date). This will give you time to find parking, check in, and get settled before we begin.",
    },
    {
      question: "Is there parking available at the venue?",
      answer:
        "Yes, CJ's Off the Square has parking available. We'll provide more specific parking details as the date approaches.",
    },
    {
      question: "Will there be an open bar?",
      answer:
        "Yes, we'll have a full open bar throughout the reception with a selection of beer, wine, and signature cocktails.",
    },
    {
      question: "What food will be served?",
      answer:
        "We'll be serving a delicious plated dinner. If you have any food allergies or dietary restrictions, please let us know in the RSVP section so we can accommodate you.",
    },
    {
      question: "When is the RSVP deadline?",
      answer:
        "Please submit your RSVP by April 7, 2026. This helps us finalize our headcount with the venue and caterer.",
    },
    {
      question: "Will the wedding be indoors or outdoors?",
      answer:
        "The ceremony and reception will both be held outdoors at CJ's Off the Square.  The reception will be covered, but open-air. Please plan accordingly based on the weather forecast!",
    },
    {
      question: "Can I take photos during the ceremony?",
      answer:
        "We're having an unplugged ceremony, which means we kindly ask that you keep your phones and cameras put away during the ceremony itself. Our professional photographer will capture all the special moments, and we'll share those with you afterward. Feel free to take all the photos you'd like during the reception!",
    },
    {
      question: "How can I get in touch if I have more questions?",
      answer:
        "Feel free to reach out to us directly! You can contact Meredith or Branham through the phone numbers or email addresses on your invitation.",
    },
    {
      question: "Will transportation be provided?",
      answer:
        "We are not arranging transportation.  We ask that guests plan accordingly for car rental or rideshare options.",
    },
  ];

  const [openIndexes, setOpenIndexes] = useState(
    () => new Set(faqs.map((_, i) => i)),
  );

  const toggleFAQ = (index) => {
    setOpenIndexes((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  return (
    <section
      id="faq"
      className="min-h-screen flex items-center justify-center relative bg-cover bg-center"
      style={{
        backgroundImage: `url(${backgroundImage})`,
      }}
    >
      <div className="absolute inset-0 bg-black bg-opacity-45"></div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-6 py-20">
        <h2 className="section-heading text-4xl md:text-5xl text-center mb-8 text-white">
          Frequently Asked Questions
        </h2>

        <p className="text-center text-gray-200 text-lg mb-12 max-w-2xl mx-auto">
          We've answered some common questions below. If you don't see your
          question here, please don't hesitate to reach out!
        </p>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-black bg-opacity-40 backdrop-blur-sm border border-white border-opacity-20 rounded-lg overflow-hidden transition-all hover:border-opacity-40"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left transition-all"
              >
                <span className="text-white font-medium text-lg pr-4">
                  {faq.question}
                </span>
                {openIndexes.has(index) ? (
                  <ChevronUp className="w-5 h-5 text-white flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-white flex-shrink-0" />
                )}
              </button>

              {openIndexes.has(index) && (
                <div className="px-6 pb-5">
                  <p className="text-gray-200 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TravelSection = ({ backgroundImage }) => {
  const hotels = [
    {
      name: "Hilton Franklin Cool Springs",
      address: "601 Corporate Centre Drive, Franklin, TN 37067",
      bookBy: "May 7, 2026",
      link: "https://www.hilton.com/en/attend-my-event/brooksleavenswedding/",
    },
    {
      name: "Drury Plaza Hotel Nashville Franklin",
      address: "1874 West McEwen Drive, Franklin, TN 37067",
      bookBy: "April 21, 2026",
      link: "https://www.druryhotels.com/bookandstay/newreservation/?groupno=10177563",
    },
    {
      name: "Staybridge Suites Franklin Cool Springs",
      address: "3601 Mallory Lane, Franklin, TN 37067",
      bookBy: "May 9, 2026",
      link: "https://www.ihg.com/staybridge/hotels/us/en/find-hotels/select-roomrate?fromRedirect=true&qSrt=sBR&qIta=99801505&icdv=99801505&qSlH=FNKTN&qCiD=06&qCiMy=052026&qCoD=08&qCoMy=052026&qGrpCd=BLW&qAAR=6CBARC&qRtP=6CBARC&setPMCookies=true&qSHBrC=SB&qDest=3601%20Mallory%20Lane,%20Franklin,%20TN,%20US&showApp=true&adjustMonth=false&srb_u=1&qRmFltr=",
    },
    {
      name: "Hyatt House Cool Springs",
      address: "3501 Mallory Lane, Franklin, TN 37067",
      bookBy: "May 6, 2026",
      link: "https://www.hyatt.com/shop/rooms/bnaxf?location=Hyatt%20House%20Nashville%20%2F%20Franklin%20-%20Cool%20Springs&checkinDate=2026-06-07&checkoutDate=2026-06-08&rooms=1&adults=1&kids=0&corp_id=G-BRLE",
    },
    {
      name: "Franklin Marriott Cool Springs",
      address: "700 Cool Springs Blvd., Franklin, TN 37067",
      bookBy: "May 15, 2026",
      link: "https://app.marriott.com/reslink?id=1769534276107&key=GRP&app=resvlink",
    },
  ];

  return (
    <section
      id="travel"
      className="min-h-screen flex items-center justify-center relative bg-cover bg-center"
      style={{
        backgroundImage: `url(${backgroundImage})`,
      }}
    >
      <div className="absolute inset-0 bg-black bg-opacity-45"></div>

      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 py-20">
        <h2 className="section-heading text-4xl md:text-5xl text-center mb-8 text-white">
          Travel & Stay
        </h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 mb-12">
          {hotels.map((hotel, index) => (
            <div
              key={index}
              className="bg-black bg-opacity-40 backdrop-blur-sm border border-white border-opacity-20 rounded-lg p-8 hover:border-opacity-40 transition-all"
            >
              <h3 className="text-xl md:text-2xl mb-6 text-white font-medium tracking-wide">
                {hotel.name}
              </h3>

              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-white mt-1 flex-shrink-0 opacity-70" />
                  <p className="text-gray-200 text-sm">{hotel.address}</p>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-white flex-shrink-0 opacity-70" />
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">
                      Book by
                    </p>
                    <p className="text-gray-100 font-light">{hotel.bookBy}</p>
                  </div>
                </div>

                <a
                  href={hotel.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-white bg-opacity-10 hover:bg-opacity-20 border border-white border-opacity-30 rounded text-white text-sm uppercase tracking-widest transition-all"
                >
                  Reserve Room
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-2xl mx-auto bg-black bg-opacity-40 backdrop-blur-sm border border-white border-opacity-20 rounded-lg p-8">
          <p className="text-gray-200 text-center leading-relaxed text-sm">
            We've negotiated special rates at these hotels for our guests.
            Please note the booking deadlines—rooms will be released from our
            block after these dates. If you have questions about availability or
            need assistance, please don't hesitate to reach out!
          </p>
        </div>
      </div>
    </section>
  );
};

const RegistrySection = ({ backgroundImage }) => {
  const [selectedGift, setSelectedGift] = React.useState(null);
  const [customAmounts, setCustomAmounts] = React.useState({});

  const honeymoonGifts = [
    {
      id: "airfare",
      label: "Round Trip Airfare",
      icon: "✈️",
      description: "Toward our flights to Japan",
    },
    {
      id: "hotel",
      label: "Hotel Stay",
      icon: "🏨",
      description: "A night in Japan",
    },
    {
      id: "meals",
      label: "Meals & Experiences",
      icon: "🍜",
      description: "Food and memories in Japan",
    },
    // {
    //   id: "excursion",
    //   label: "Adventure Excursion",
    //   icon: "⛩️",
    //   description: "An experience in Japan",
    // },
    // {
    //   id: "shopping",
    //   label: "Honeymoon Spending",
    //   icon: "🗾",
    //   description: "Shopping and exploring Japan",
    // },
  ];

  const homeGifts = [
    {
      id: "rug",
      label: "New Rug",
      icon: "🧶",
      description: "For our new home",
    },
    {
      id: "walldecor",
      label: "Wall Decor",
      icon: "🖼️",
      description: "To brighten our walls",
    },
    {
      id: "furniture",
      label: "Furniture",
      icon: "🛋️",
      description: "To furnish our home",
    },
  ];

  const handleAmountChange = (giftId, amount) => {
    setCustomAmounts({
      ...customAmounts,
      [giftId]: amount,
    });
  };

  const generateVenmoLink = (gift, amount) => {
    const venmoUsername = "Branham-Leavens";
    const note = `${gift.label}`;
    // Convert to number and trim any whitespace
    const cleanAmount = String(amount).trim();
    return `https://venmo.com/?txn=pay&recipients=${venmoUsername}&amount=${cleanAmount}&note=${encodeURIComponent(note)}`;
  };

  const getAmount = (giftId) => customAmounts[giftId] || "";

  return (
    <section
      id="registry"
      className="min-h-screen flex items-center justify-center relative bg-cover bg-center"
      style={{
        backgroundImage: `url(${backgroundImage})`,
      }}
    >
      <div className="absolute inset-0 bg-black bg-opacity-45"></div>

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 py-20">
        <h2 className="section-heading text-4xl md:text-5xl text-center mb-8 text-white">
          Registry
        </h2>

        <p className="text-center text-gray-200 text-lg mb-16 max-w-2xl mx-auto">
          Your love and presence mean the world to us. If you'd like to give a
          gift, we've created a few options below.
        </p>

        {/* Amazon Registry Card */}
        <h3 className="text-2xl md:text-3xl text-center mb-12 text-white font-medium tracking-wide">
          Amazon Registry
        </h3>

        <div className="bg-black bg-opacity-40 backdrop-blur-sm border border-white border-opacity-20 rounded-lg p-6 hover:border-opacity-40 transition-all text-center flex flex-col items-center">
          <div className="text-4xl mb-3">🎁</div>
          <h4 className="text-white font-medium text-sm mb-1">
            Amazon Registry
          </h4>
          <p className="text-gray-300 text-xs mb-4">
            Curated items to help us build our home
          </p>
          <a
            href="https://www.amazon.com/wedding/guest-view/1SW6FAJJ8XSVV"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 bg-white bg-opacity-10 hover:bg-opacity-20 border border-white border-opacity-30 rounded text-white text-xs uppercase tracking-widest transition-all w-full justify-center"
          >
            View Registry
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <br></br>
        <br></br>

        {/* Honeymoon to Japan Section */}
        <div className="mb-16">
          <h3 className="text-2xl md:text-3xl text-center mb-12 text-white font-medium tracking-wide">
            Honeymoon to Japan
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {honeymoonGifts.map((gift) => (
              <div
                key={gift.id}
                className="bg-black bg-opacity-40 backdrop-blur-sm border border-white border-opacity-20 rounded-lg p-6 hover:border-opacity-40 transition-all"
              >
                <div className="text-4xl mb-3">{gift.icon}</div>
                <h4 className="text-white font-medium text-sm mb-1">
                  {gift.label}
                </h4>
                <p className="text-gray-300 text-xs mb-4">{gift.description}</p>
                <div className="flex items-center gap-2 h-10">
                  <span className="text-white text-sm">$</span>
                  <input
                    type="number"
                    min="1"
                    value={getAmount(gift.id)}
                    onChange={(e) =>
                      handleAmountChange(gift.id, e.target.value)
                    }
                    placeholder="0"
                    className="w-full bg-white bg-opacity-10 border border-white border-opacity-30 rounded px-2 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-opacity-60 transition-all text-sm"
                  />
                </div>
                {getAmount(gift.id) && (
                  <a
                    href={generateVenmoLink(gift, getAmount(gift.id))}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-3 px-3 py-2 bg-white bg-opacity-10 hover:bg-opacity-20 border border-white border-opacity-30 rounded text-white text-xs uppercase tracking-widest transition-all w-full justify-center"
                  >
                    Send
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Build Our Home Section */}
        <div className="mb-16">
          <h3 className="text-2xl md:text-3xl text-center mb-12 text-white font-medium tracking-wide">
            Help Us Build Our Home
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {homeGifts.map((gift) => (
              <div
                key={gift.id}
                className="bg-black bg-opacity-40 backdrop-blur-sm border border-white border-opacity-20 rounded-lg p-6 hover:border-opacity-40 transition-all"
              >
                <div className="text-4xl mb-3">{gift.icon}</div>
                <h4 className="text-white font-medium text-sm mb-1">
                  {gift.label}
                </h4>
                <p className="text-gray-300 text-xs mb-4">{gift.description}</p>
                <div className="flex items-center gap-2 h-10">
                  <span className="text-white text-sm">$</span>
                  <input
                    type="number"
                    min="1"
                    value={getAmount(gift.id)}
                    onChange={(e) =>
                      handleAmountChange(gift.id, e.target.value)
                    }
                    placeholder="0"
                    className="w-full bg-white bg-opacity-10 border border-white border-opacity-30 rounded px-2 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-opacity-60 transition-all text-sm"
                  />
                </div>
                {getAmount(gift.id) && (
                  <a
                    href={generateVenmoLink(gift, getAmount(gift.id))}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-3 px-3 py-2 bg-white bg-opacity-10 hover:bg-opacity-20 border border-white border-opacity-30 rounded text-white text-xs uppercase tracking-widest transition-all w-full justify-center"
                  >
                    Send
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-2xl mx-auto bg-black bg-opacity-40 backdrop-blur-sm border border-white border-opacity-20 rounded-lg p-8">
          <p className="text-gray-200 text-center leading-relaxed text-sm">
            Please know that your attendance at our celebration is the greatest
            gift of all. We're grateful for whatever you choose to give or not
            give—your support means everything.
          </p>
        </div>
      </div>
    </section>
  );
};

export default function WeddingWebsite() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Login state
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // RSVP state
  const [rsvpData, setRsvpData] = useState({
    guests: [], // array of { name, attending: "yes" | "no" }
    any_allergies: "",
    message: "",
  });

  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);

  useEffect(() => {
    const savedUser = sessionStorage.getItem("weddingUser");
    if (savedUser) {
      const user = JSON.parse(savedUser);
      console.log("guest_list:", user.guest_list);

      setCurrentUser(user);
      loadUserRSVP(user);
    }
    setIsLoading(false);
  }, []);

  const handleLogin = async () => {
    setLoginError("");

    try {
      // Authenticate user via your RPC
      const { data, error } = await supabase.rpc(
        "authenticate_guest_by_password",
        { password_input: password },
      );
      console.log("RPC data:", data);

      if (error || !data || data.length === 0) {
        setLoginError("Invalid password");
        return;
      }

      const userData = data[0];

      // Fetch 'has_responded' from wedding_users
      const { data: userRecord, error: userError } = await supabase
        .from("wedding_users")
        .select("has_responded")
        .eq("guest_id", userData.guest_id);

      if (userError)
        console.error("Error fetching user responded flag:", userError);

      const fullUserData = {
        ...userData,
        has_responded:
          (userRecord &&
            userRecord.length > 0 &&
            userRecord[0].has_responded) ||
          false,
      };

      // Save user to sessionStorage
      sessionStorage.setItem("weddingUser", JSON.stringify(fullUserData));
      setCurrentUser(fullUserData);

      // Load RSVP info
      await loadUserRSVP(fullUserData);
    } catch (err) {
      console.error("Login error:", err);
      setLoginError("Connection error");
    }
  };

  const loadUserRSVP = async (user) => {
    try {
      // Fetch RSVP for this guest
      const { data: rsvps } = await supabase
        .from("wedding_rsvps")
        .select("*")
        .eq("guest_id", user.guest_id);

      console.log("User data:", user);
      console.log("Fetched RSVPs:", rsvps);

      const rsvpExists = rsvps.length > 0;

      let rsvpDataTemp = {
        numGuests: 1,
        any_allergies: "",
        message: "",
        guests: [], // we'll populate from guest_list
      };

      if (user.guest_list) {
        // Split guest_list string into array of guest objects
        if (user.guest_list?.trim()) {
          const guestsArray = user.guest_list
            .split(",")
            .map((name) => ({ name: name.trim(), attending: "yes" }));
          rsvpDataTemp.guests = guestsArray;
        }
      }

      if (rsvpExists) {
        const rsvp = rsvps[0];

        // Check if guests are objects or strings
        rsvpDataTemp.guests = rsvp.guests.map((guest) => {
          if (typeof guest === "string") {
            return { name: guest, attending: "yes" };
          } else if (guest.name) {
            return { name: guest.name, attending: guest.attending || "yes" };
          } else {
            return { name: "Guest", attending: "yes" };
          }
        });
      }

      if (!rsvpExists && user.guest_list?.trim()) {
        rsvpDataTemp.guests = user.guest_list
          .split(",")
          .map((name) => ({ name: name.trim(), attending: "yes" }));
        console.log("Initialized guests from guest_list:", rsvpDataTemp.guests);
      }

      setRsvpData(rsvpDataTemp);

      // Update user state with has_responded
      const updatedUser = { ...user, has_responded: rsvpExists };
      setCurrentUser(updatedUser);

      // Save back to session
      sessionStorage.setItem("weddingUser", JSON.stringify(updatedUser));
    } catch (err) {
      console.error("Failed to load RSVP:", err);
      setCurrentUser(user); // fallback
    }
  };

  const handleRSVPSubmit = async () => {
    try {
      const payload = {
        guest_id: currentUser.guest_id,
        any_allergies: rsvpData.any_allergies,
        message: rsvpData.message,
        guests: rsvpData.guests, // save full array with individual yes/no
      };

      const { data: existing } = await supabase
        .from("wedding_rsvps")
        .select("id")
        .eq("guest_id", currentUser.guest_id);

      if (existing && existing.length > 0) {
        await supabase
          .from("wedding_rsvps")
          .update(payload)
          .eq("guest_id", currentUser.guest_id);
      } else {
        await supabase.from("wedding_rsvps").insert(payload);
      }

      // Mark as responded in wedding_users
      const { data, error } = await supabase
        .from("wedding_users")
        .update({ has_responded: true })
        .eq("guest_id", currentUser.guest_id);
      console.log("Update response:", data, error);

      // Update local state so form no longer shows
      setRsvpSubmitted(true);
      setCurrentUser({ ...currentUser, has_responded: true });

      // Update sessionStorage
      sessionStorage.setItem(
        "weddingUser",
        JSON.stringify({ ...currentUser, has_responded: true }),
      );
    } catch (error) {
      console.error("RSVP error:", error);
      alert("Failed to submit RSVP");
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  const handleKeyPress = (e, action) => {
    if (e.key === "Enter") {
      action();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        Loading...
      </div>
    );
  }

  // Login Screen
  if (!currentUser) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `url(${cjs})`,
        }}
      >
        <div className="max-w-md w-full mx-4 p-8 bg-black bg-opacity-50 backdrop-blur-lg rounded-lg border border-white border-opacity-20">
          {/* <h1 className="couple-names text-5xl text-center mb-8 text-white"></h1> */}
          <p className="text-center text-white text-opacity-80 mb-6 text-sm">
            Please enter the password from your invitation
          </p>
          {loginError && (
            <div className="bg-red-500 bg-opacity-20 border border-red-500 text-red-200 px-4 py-2 rounded mb-4 text-sm">
              {loginError}
            </div>
          )}

          <div className="space-y-4">
            {" "}
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, handleLogin)}
              placeholder="Password"
              className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-30 rounded text-white placeholder-gray-300 focus:outline-none"
            />
            <button
              onClick={handleLogin}
              className="w-full px-6 py-3 bg-white bg-opacity-20 hover:bg-opacity-30 border border-white border-opacity-40 rounded text-white uppercase tracking-widest transition-all"
            >
              Enter
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main Wedding Site
  return (
    <div className="min-h-screen bg-black text-white">
      <style>{`
        body {
          font-family: 'Montserrat', sans-serif;
          font-weight: 300;
        }
        .couple-names {
          font-family: 'Alex Brush', cursive;
          font-weight: 400;
        }
        .section-heading {
          font-family: 'Lora', serif;
          font-weight: 500;
          letter-spacing: 0.05em;
        }
        input::placeholder, textarea::placeholder {
          opacity: 0.7;
        }
      `}</style>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black bg-opacity-30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-xl tracking-wider">M & B</div>

            <div className="hidden md:flex space-x-8">
              {["home", "details", "rsvp", "travel", "faq", "registry"].map(
                (item) => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item)}
                    className="text-xs uppercase tracking-wide hover:text-gray-300 transition-colors"
                  >
                    {item}
                  </button>
                ),
              )}
            </div>

            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              {["home", "details", "travel", "rsvp", "faq", "registry"].map(
                (item) => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item)}
                    className="block w-full text-left text-xs uppercase tracking-wide hover:text-gray-300 transition-colors"
                  >
                    {item}
                  </button>
                ),
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Home */}
      <section
        id="home"
        className="min-h-screen flex items-center justify-center relative bg-cover bg-center"
        style={{
          backgroundImage: `url(${img1})`,
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-45"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="couple-names text-7xl md:text-9xl mb-6">
            Meredith & Branham
          </h1>
          <p
            className="text-lg md:text-xl tracking-widest mb-8"
            style={{ letterSpacing: "0.3em" }}
          >
            JUNE 7, 2026
          </p>
          <Heart className="mx-auto" size={32} />
        </div>
      </section>

      {/* Details */}
      <section
        id="details"
        className="min-h-screen flex items-center justify-center relative bg-cover bg-center"
        style={{
          backgroundImage: `url(${cjs})`,
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-45"></div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 py-20">
          <h2 className="section-heading text-4xl md:text-5xl mb-16 text-center">
            Event Details
          </h2>
          <div className="text-center">
            <Calendar className="mx-auto mb-6" size={40} />
            <h3 className="section-heading text-2xl mb-6">
              Ceremony & Reception
            </h3>
            <p className="text-gray-200 mb-1">Sunday, June 7, 2026</p>
            <p className="text-gray-200 mb-6">
              Please Arrive by 4:30 PM for a 5:00 PM Ceremony
            </p>
            <p className="text-gray-200 font-medium">CJ's Off the Square</p>
            <p className="text-gray-200">218 3rd Ave N</p>
            <p className="text-gray-200">Franklin, TN 37064</p>
          </div>
        </div>
      </section>

      {/* RSVP */}
      <section
        id="rsvp"
        className="min-h-screen flex items-center justify-center relative bg-cover bg-center"
        style={{
          backgroundImage: `url(${img2})`,
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-55"></div>
        <div className="relative z-10 max-w-2xl mx-auto px-6 py-20">
          <h2 className="section-heading text-4xl md:text-5xl mb-12 text-center">
            RSVP for {currentUser.name}
          </h2>
          <p className="text-center mb-8 text-gray-300"></p>

          {currentUser.has_responded || rsvpSubmitted ? (
            <div className="text-center text-xl">
              Thank you! We've received your response.
            </div>
          ) : (
            <div className="space-y-5">
              {rsvpData.guests && rsvpData.guests.length > 0 && (
                <div className="space-y-4">
                  <p className="text-white text-sm mb-2">
                    RSVP for each guest by April 19th:
                  </p>
                  {rsvpData.guests.map((guest, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <span className="w-32">{guest.name}</span>
                      <select
                        value={guest.attending}
                        onChange={(e) => {
                          const newGuests = [...rsvpData.guests];
                          newGuests[index].attending = e.target.value;
                          setRsvpData({ ...rsvpData, guests: newGuests });
                        }}
                        className="bg-white bg-opacity-10 border border-white border-opacity-30 rounded px-3 py-2 text-white focus:outline-none"
                      >
                        <option value="yes" className="text-black">
                          Joyfully Accepts
                        </option>
                        <option value="no" className="text-black">
                          Regretfully Declines
                        </option>
                      </select>
                    </div>
                  ))}
                </div>
              )}

              <input
                type="text"
                value={rsvpData.any_allergies}
                onChange={(e) =>
                  setRsvpData({ ...rsvpData, any_allergies: e.target.value })
                }
                placeholder="Any Food Allergies? (optional)"
                className="w-full bg-white bg-opacity-10 border border-white border-opacity-30 rounded px-4 py-3 text-white placeholder-gray-300 focus:outline-none focus:border-opacity-60 transition-all"
              />

              <textarea
                value={rsvpData.message}
                onChange={(e) =>
                  setRsvpData({ ...rsvpData, message: e.target.value })
                }
                placeholder="Message to the couple! (optional)"
                rows={4}
                className="w-full bg-white bg-opacity-10 border border-white border-opacity-30 rounded px-4 py-3 text-white placeholder-gray-300 focus:outline-none focus:border-opacity-60 resize-none transition-all"
              />

              <button
                onClick={handleRSVPSubmit}
                className="w-full bg-white bg-opacity-20 hover:bg-opacity-30 border border-white border-opacity-30 rounded px-6 py-3 text-white uppercase tracking-widest transition-all"
              >
                Submit RSVP
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Travel */}
      <TravelSection backgroundImage={franklin} />

      {/* FAQ */}
      <FAQSection backgroundImage={img4} />

      {/* Registry */}
      <RegistrySection backgroundImage={img3} />
    </div>
  );
}
